import React, { useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import {
  GQLSecurityContext,
  GQLPartyType,
  GQLAuthentication
} from '@jewelutions/core-crm-server-api-client-domain';

import { AuthContext, AuthenticationStorage } from './core/AuthContext';
import Login from './modules/common-modules/login/Login';

import './i18nextInit';

import AuthLayout from './layouts/Auth';
import ProducerRoutes from './modules/producer';
import PlatformRoutes from './modules/platform/navigation/PlatformRoutes';
import MerchantRoutes from './modules/merchant/navigation/MerchantRoutes';

import { ProducerNavigation } from './modules/producer/navigation/ProducerNavigation';
import { PlatformNavigation } from './modules/platform/navigation/PlatformNavigation';
import { MerchantNavigation } from './modules/merchant/navigation/MerchantNavigation';

export const AppRoutes: React.FC = () => {
  const [auth, setAuth] = useState<GQLSecurityContext | null>(
    AuthenticationStorage.getAuthentication() !== null
      ? AuthenticationStorage.getAuthentication()!.securityContext
      : null
  );
  const [token, setToken] = useState<string | null>(null);

  const isAuthenticated = () => {
    if (!auth) {
      console.log(`Just checked and not authenticated. Logging out.`);
      return false;
    }
    if (new Date(Date.parse(auth.expiresAt)).getTime() <= new Date().getTime()) {
      /* Expired */
      console.log(`Expired security context. Logging out.`);
      unsetAuthentication();
      return false;
    }

    return true;
  };

  const unsetAuthentication = () => {
    AuthenticationStorage.removeAuthentication();
    setAuth(null);
    setToken(null);
  };

  const setAuthentication = (value: GQLAuthentication) => {
    AuthenticationStorage.setAuthentication(value);
    setAuth(value.securityContext);
    setToken(value.token);
  };

  return (
    <AuthContext.Provider value={{ token, auth, setAuthentication, unsetAuthentication, isAuthenticated }}>
      <Switch>{isAuthenticated() ? <AuthenticatedAppRoutes /> : <UnauthenticatedAppRoutes />}</Switch>
    </AuthContext.Provider>
  );
};

const AuthenticatedAppRoutes: React.FC<any> = ({}: any) => {
  return (
    <AuthContext.Consumer>
      {authenticationContext => (
        <Switch>
          <Route exact path="/">
            <Redirect to={getDefaultRouteByPartyType(authenticationContext.auth!.partyType)} />
          </Route>
          {authenticationContext.auth!.partyType === GQLPartyType.Producer && <ProducerRoutes />}
          {authenticationContext.auth!.partyType === GQLPartyType.Platform && <PlatformRoutes />}
          {authenticationContext.auth!.partyType === GQLPartyType.Merchant && <MerchantRoutes />}
        </Switch>
      )}
    </AuthContext.Consumer>
  );
};

const UnauthenticatedAppRoutes: React.FC<any> = ({}: any) => {
  return (
    <React.Fragment>
      <Redirect to="/" />
      <Route
        path="/"
        render={() => (
          <AuthLayout>
            <Login />
          </AuthLayout>
        )}
      />
    </React.Fragment>
  );
};

export const getDefaultRouteByPartyType = (partyType: GQLPartyType) => {
  switch (partyType) {
    case GQLPartyType.Producer:
      return ProducerNavigation.symbolCollectionList.pathFromParams({});
    case GQLPartyType.Platform:
      return PlatformNavigation.platformMerchantContracts.pathFromParams({});
    case GQLPartyType.Merchant:
      return MerchantNavigation.merchantChannelSettingsOverall.pathPattern();
    default:
      return ProducerNavigation.symbolCollectionList.pathFromParams({});
  }
};
